import { useState } from 'react'
import { X, Copy, Check, Link as LinkIcon } from 'lucide-react'
import { useVideoStore } from '../../stores/videoStore'
import { useClipboard } from '../../hooks'
import type { CreateLinkFormData, TrackingLink } from '../../types'

interface LinkCreatorProps {
  videoId: string
  onClose: () => void
  onSuccess?: (link: TrackingLink) => void
}

export default function LinkCreator({ videoId, onClose, onSuccess }: LinkCreatorProps) {
  const { createLink, isLoading, error } = useVideoStore()
  const { copied, copy } = useClipboard()
  const [createdLink, setCreatedLink] = useState<TrackingLink | null>(null)

  const [formData, setFormData] = useState<CreateLinkFormData>({
    recipient_name: '',
    recipient_email: '',
    recipient_company: '',
    custom_message: '',
    custom_cta_text: '',
    custom_cta_url: '',
    notify_on_view: true,
    notify_on_complete: true,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const link = await createLink(videoId, formData)
      setCreatedLink(link)
      onSuccess?.(link)
    } catch {
      // Error is handled in store
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }))
  }

  if (createdLink) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-xl max-w-md w-full p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold text-white">Link Created!</h2>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">
                Share this link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={createdLink.short_url}
                  readOnly
                  className="input flex-1"
                />
                <button
                  onClick={() => copy(createdLink.short_url)}
                  className="btn-primary"
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {createdLink.recipient_name && (
              <p className="text-sm text-slate-400">
                Created for: <span className="text-white">{createdLink.recipient_name}</span>
              </p>
            )}

            <button onClick={onClose} className="btn-secondary w-full">
              Done
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl max-w-lg w-full p-6 border border-slate-700 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-primary-400" />
            </div>
            <h2 className="text-lg font-semibold text-white">Create Tracking Link</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Recipient info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Recipient Name
              </label>
              <input
                type="text"
                name="recipient_name"
                value={formData.recipient_name}
                onChange={handleChange}
                placeholder="John Doe"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Recipient Email
              </label>
              <input
                type="email"
                name="recipient_email"
                value={formData.recipient_email}
                onChange={handleChange}
                placeholder="john@example.com"
                className="input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Company
            </label>
            <input
              type="text"
              name="recipient_company"
              value={formData.recipient_company}
              onChange={handleChange}
              placeholder="Acme Inc."
              className="input"
            />
          </div>

          {/* Custom message */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Custom Message
            </label>
            <textarea
              name="custom_message"
              value={formData.custom_message}
              onChange={handleChange}
              placeholder="Add a personalized message..."
              rows={3}
              className="input resize-none"
            />
          </div>

          {/* CTA */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                CTA Button Text
              </label>
              <input
                type="text"
                name="custom_cta_text"
                value={formData.custom_cta_text}
                onChange={handleChange}
                placeholder="Learn More"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                CTA URL
              </label>
              <input
                type="url"
                name="custom_cta_url"
                value={formData.custom_cta_url}
                onChange={handleChange}
                placeholder="https://example.com"
                className="input"
              />
            </div>
          </div>

          {/* Notifications */}
          <div className="space-y-3 pt-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="notify_on_view"
                checked={formData.notify_on_view}
                onChange={handleChange}
                className="rounded border-slate-600 bg-slate-700 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm text-slate-300">Notify me when video is viewed</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="notify_on_complete"
                checked={formData.notify_on_complete}
                onChange={handleChange}
                className="rounded border-slate-600 bg-slate-700 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm text-slate-300">Notify me on high engagement</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-ghost">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? 'Creating...' : 'Create Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
